import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { getRestaurantOwnerAnalytics } from "../../../util/analyticsApi";
import EngagementFunnel from "../administration/analytics/EngagementFunnel";
import SearchVolumeChart from "../administration/analytics/SearchVolumeChart";
import PopularKeywordsChart from "../administration/analytics/PopularKeywordsChart";
import ZeroResultQueries from "../administration/analytics/ZeroResultQueries";
import CuisineDemandChart from "../administration/analytics/CuisineDemandChart";

const Container = styled.div`
  padding: 24px;
`;

const PresetBar = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const PresetButton = styled.button`
  padding: 6px 14px;
  border: 1px solid ${(props) => (props.$active ? "#4caf50" : "#ddd")};
  border-radius: 4px;
  background: ${(props) => (props.$active ? "#4caf50" : "white")};
  color: ${(props) => (props.$active ? "white" : "#333")};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  &:hover {
    background: ${(props) => (props.$active ? "#43a047" : "#f5f5f5")};
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  background: #f5f7fa;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
`;

const CardValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #212121;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: #757575;
  margin-top: 4px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #212121;
  margin: 0 0 12px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  padding: 16px;
  background: #ffebee;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const PRESETS = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "1y": 365,
};

const getDateRange = (preset) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - PRESETS[preset]);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0],
  };
};

const RestaurantOwnerAnalytics = ({ restaurantId, restaurantName }) => {
  const [timePreset, setTimePreset] = useState("30d");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = getDateRange(timePreset);
      const result = await getRestaurantOwnerAnalytics(restaurantId, filters);
      setData(result);
    } catch (err) {
      setError(err.response?.data?.errors?.[0]?.msg || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [restaurantId, timePreset]);

  useEffect(function fetchAnalyticsDataOnPresetChange() {
    fetchData();
  }, [fetchData]);

  const funnel = data?.funnel;

  return (
    <Container>
      <PresetBar>
        {Object.keys(PRESETS).map((preset) => (
          <PresetButton
            key={preset}
            $active={timePreset === preset}
            onClick={() => setTimePreset(preset)}
          >
            {preset}
          </PresetButton>
        ))}
      </PresetBar>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <SummaryCards>
        <SummaryCard>
          <CardValue>{funnel?.restaurantClicks ?? "—"}</CardValue>
          <CardLabel>Clicks</CardLabel>
        </SummaryCard>
        <SummaryCard>
          <CardValue>{funnel?.restaurantDetailsClicks ?? "—"}</CardValue>
          <CardLabel>Details Views</CardLabel>
        </SummaryCard>
        <SummaryCard>
          <CardValue>{funnel?.restaurantWebsiteClicks ?? "—"}</CardValue>
          <CardLabel>Website Clicks</CardLabel>
        </SummaryCard>
        <SummaryCard>
          <CardValue>{funnel?.restaurantDirectionsClicks ?? "—"}</CardValue>
          <CardLabel>Directions</CardLabel>
        </SummaryCard>
        <SummaryCard>
          <CardValue>{data?.cityUniqueSearchers ?? "—"}</CardValue>
          <CardLabel>Area Searches</CardLabel>
        </SummaryCard>
      </SummaryCards>

      <Section>
        <SectionTitle>Engagement Funnel</SectionTitle>
        <EngagementFunnel
          data={funnel}
          loading={loading}
          currentRestaurantId={restaurantId}
          onRestaurantFilterChange={() => {}}
        />
      </Section>

      <Section>
        <SectionTitle>Search Volume in Your Area</SectionTitle>
        <SearchVolumeChart data={data?.citySearchVolume} loading={loading} />
      </Section>

      <Section>
        <SectionTitle>Popular Keywords in Your Area</SectionTitle>
        <PopularKeywordsChart data={data?.cityPopularKeywords} loading={loading} />
      </Section>

      <Section>
        <SectionTitle>Searches With No Results</SectionTitle>
        <ZeroResultQueries data={data?.cityZeroResultQueries} loading={loading} />
      </Section>

      <Section>
        <SectionTitle>Cuisine Demand in Your Area</SectionTitle>
        <CuisineDemandChart data={data?.cityCuisineDemand} loading={loading} />
      </Section>
    </Container>
  );
};

export default RestaurantOwnerAnalytics;
